import "react-toastify/dist/ReactToastify.css";
import "../Inspection.css";
import { VesselInformation } from "../components/VesselInformation";
import { DownloadAndShare } from "../components/DownloadAndShare";
import { Tabs } from "../components/tab-bar/Tabs";
import { useTranslation } from "react-i18next";
import { useVesselInspectionData } from "../queries/particularsQueries";
import { useParams } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { InspectionViewType } from "../types/InspectionViewType";
import { getTabsForInspection } from "./InspectionTabs";
import { DataSharingOptIn } from "@idwal/idwal-react-components";
import { checkIfIdUser } from "../utils/userGroups";
import { useCallback, useEffect, useState } from "react";
import { InspectionSideBar } from "./InspectionSideBar";
import { updateOptIn } from "../services/InspectionService";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
    inspectionsDataSharingStatusSelector,
    inspectionsToggleDataSharingOptInSelector,
    setDataSharingStatus,
    setSelectedInspection,
    setToggleDataSharingOptIn
} from "../features/inspectionsSlice";
import { useDownloads } from "../queries/downloadQueries";
import { DataSharingStatus } from "../constants/DataSharingConstants";
import { canShowDataSharingOptIn } from "../utils/dataSharingHelpers";

export const RESALE_REPORT_SHORT_NAME = "PSR";

export default function Inspection(props: {
    readonly view?: InspectionViewType;
}) {
    const [sideBarOpen, setSideBarOpen] = useState(false);
    const { t } = useTranslation("locale");
    const { inspectionId } = useParams();
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);

    const { data, isError, isLoading, error } = useVesselInspectionData(
        inspectionId!,
        user
    );

    const toggleDataSharingOptIn = useAppSelector(
        inspectionsToggleDataSharingOptInSelector
    );
    const dataSharingStatus = useAppSelector(
        inspectionsDataSharingStatusSelector
    );

    const { data: documentData } = useDownloads(inspectionId ?? "", user);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (data) {
            const shouldShowOptIn = canShowDataSharingOptIn(
                data.dataSharingStatus,
                data.dataSharingAvailable,
                data.InspectionTypeShortName
            );

            dispatch(setSelectedInspection(data.inspectionNumber));
            dispatch(setToggleDataSharingOptIn(shouldShowOptIn));
            dispatch(setDataSharingStatus(data.dataSharingStatus));
        }
    }, [data]);

    const isIdUser = checkIfIdUser(user);

    const onAgree = useCallback(() => {
        dispatch(setToggleDataSharingOptIn(false));
        updateOptIn(inspectionId!, "True", user);
        dispatch(setDataSharingStatus(DataSharingStatus.OPTED_IN));
    }, [inspectionId]);

    const onDecline = useCallback(() => {
        dispatch(setToggleDataSharingOptIn(false));
        updateOptIn(inspectionId!, "False", user);
        dispatch(setDataSharingStatus(DataSharingStatus.INSPECTION_OPT_OUT));
    }, [inspectionId]);

    return (
        <div>
            <DataSharingOptIn
                className="mb-4"
                inspectionType={data?.InspectionType ?? ""}
                vesselName={data?.ShipName ?? ""}
                onAgree={onAgree}
                onDecline={onDecline}
                visible={!!toggleDataSharingOptIn}
            />
            <div className={"inspection-page-wrapper"}>
                <div>
                    <VesselInformation />
                    <div className="relative mt-6">
                        <div className="z-1 download-button-responsive flex justify-content-center">
                            <DownloadAndShare
                                data={data}
                                dataSharingStatusOverride={dataSharingStatus}
                            />
                        </div>
                        <div className="mt-3 z-0">
                            <Tabs
                                selectedTab={props.view}
                                tabs={getTabsForInspection(
                                    {
                                        vesselInspectionResponse: data,
                                        isError: isError,
                                        isLoading: isLoading,
                                        error: error
                                    },
                                    documentData,
                                    t,
                                    dataSharingStatus
                                )}
                            ></Tabs>
                        </div>
                    </div>
                </div>
                <InspectionSideBar
                    sideBarOpen={sideBarOpen}
                    setSideBarOpen={setSideBarOpen}
                    inspectionType={data?.InspectionType}
                    isIdUser={isIdUser}
                />
            </div>
        </div>
    );
}
